 
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare, X, CheckCircle, Clock, User, Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import { useSupportChat, SupportMessage } from '../../hooks/useSupportChat';
import { useNotifications } from '../../hooks/useNotifications';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { usePresenceHistory } from '../../hooks/usePresenceHistory';
import { Button } from '../Button';
import { Input } from '../Input';
import { toast } from 'sonner';
import { supabase } from '../../providers/AuthProvider';

interface SupportChatProps {
  ticketId: string;
  onClose?: () => void;
}

export function SupportChat({ ticketId, onClose }: SupportChatProps) {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { messages, ticket, loading, sending, sendMessage, updateTicketStatus, onlineUsers, typingUsers, sendTyping } = useSupportChat(ticketId);

  // Scroll para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Marcar mensagens como lidas quando visualizadas
  useEffect(() => {
    if (messages.length > 0) {
      const unreadMessages = messages
        .filter((m) => !m.is_from_admin && !m.read_at)
        .map((m) => m.id);
      
      if (unreadMessages.length > 0) {
        // Marcar como lidas
        supabase
          .from('support_messages')
          .update({ read_at: new Date().toISOString() })
          .in('id', unreadMessages)
          .then(() => {
            if (updateUnreadCount) updateUnreadCount();
          });
      }
    }
  }, [messages]);

  // Notificar sobre novas mensagens
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    const isNewMessage = !lastMessage.is_from_admin && !lastMessage.read_at;
    const isRecent = new Date(lastMessage.created_at).getTime() > Date.now() - 5000;

    if (isNewMessage && isRecent && document.hidden) {
      showNotification(
        `Nova mensagem de ${ticket?.user?.email || 'cliente'}`,
        {
          body: lastMessage.message.substring(0, 100) + (lastMessage.message.length > 100 ? '...' : ''),
          tag: `support-${ticketId}`,
        }
      );
 
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, ticketId, ticket, showNotification]);

  const handleSendMessage = async () => {
    if (!message.trim() || sending) return;

    try {
      await sendMessage(message);
      setMessage('');
      inputRef.current?.focus();
    } catch (error) {
      // Erro já tratado no hook
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Agora';
    if (minutes < 60) return `${minutes}m atrás`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h atrás`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'text-red-400';
      case 'in_progress':
        return 'text-blue-400';
      case 'resolved':
        return 'text-emerald-400';
      case 'closed':
        return 'text-slate-400';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3 flex-1">
          <MessageSquare className="w-5 h-5 text-emerald-400" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">
              {ticket?.subject || 'Carregando...'}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              {ticket?.user && (
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <User className="w-3 h-3" />
                  <span className="truncate">{ticket.user.email}</span>
                </div>
              )}
              {ticket && (
                <>
                  <span className="text-slate-600">•</span>
                  <span className={`text-xs capitalize ${getStatusColor(ticket.status)}`}>
                    {ticket.status === 'open' && 'Aberto'}
                    {ticket.status === 'in_progress' && 'Em andamento'}
                    {ticket.status === 'resolved' && 'Resolvido'}
                    {ticket.status === 'closed' && 'Fechado'}
                  </span>
                </>
              )}
              {/* Online status */}
              {onlineUsers.some(u => !u.isAdmin) && (
                <>
                  <span className="text-slate-600">•</span>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-xs text-emerald-400">Cliente online</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Unread badge */}
          {unreadCount > 0 && (
            <div className="relative">
              <MessageSquare className="w-5 h-5 text-emerald-400" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-bold">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </div>
          )}
          {ticket && (
 
            <select
              value={ticket.status}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(e) => updateTicketStatus(e.target.value as any)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border border-white/10 bg-white/5 text-white ${getStatusColor(ticket.status)}`}
            >
              <option value="open">Aberto</option>
              <option value="in_progress">Em andamento</option>
              <option value="resolved">Resolvido</option>
              <option value="closed">Fechado</option>
            </select>
          )}
          {/* Notifications toggle */}
          {permission?.default && (
            <button
              onClick={requestPermission}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
              title="Ativar notificações"
            >
              <BellOff className="w-4 h-4 text-slate-400" />
            </button>
          )}
          {/* Sound toggle */}
          <button
            onClick={toggleSound}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            title={soundEnabled ? 'Desativar som' : 'Ativar som'}
          >
            {soundEnabled ? (
              <Volume2 className="w-4 h-4 text-emerald-400" />
            ) : (
              <VolumeX className="w-4 h-4 text-slate-400" />
            )}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <MessageSquare className="w-12 h-12 mb-4 opacity-50" />
            <p>Nenhuma mensagem ainda</p>
          </div>
        ) : (
          messages.map((msg: SupportMessage) => {
            const isFromAdmin = msg.is_from_admin;
            return (
              <div
                key={msg.id}
                className={`flex ${isFromAdmin ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                    isFromAdmin
                      ? 'bg-emerald-500/20 text-white'
                      : 'bg-white/5 text-slate-300'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatTime(msg.created_at)}
                    {isFromAdmin && ' • Você'}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (e.target.value.trim()) {
                sendTyping();
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Digite sua resposta..."
            disabled={sending}
            className="flex-1"
          />
          <Button
            onClick={handleSendMessage}
            disabled={!message.trim() || sending}
            size="sm"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
